/* eslint-disable arrow-body-style */
import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useMemoizedFn } from 'ahooks';
import { cloneDeep, merge } from 'lodash-es';

import NewRegionSelect from '@home/components/NewRegionSelect';
import useIntlExtend from '../../hooks/useIntlExtend';
import { analysisDataSourceMetricKey } from '@intelligence/utils/topchartsUtils';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import { RADIO_OPTIONS } from './constants';
import LOCALES from './locales';

import type { IBaseProps, IRegionType } from '@intelligence/types/common';
import type { OptionsItemType, ShortcutOptionType } from '@home/components/NewRegionSelect/type';
import type { ILanguage } from './types';

import './index.scss';

interface IRegionSelectValue {
  type: IRegionType;
  regions: string[];
  markets: string[];
}

const getValueOfRegionSelect = (
  payload: string[],
  options: OptionsItemType[],
  joiner: string,
  resolveMissingCategoryAsMarket = false,
): IRegionSelectValue => {
  const result: IRegionSelectValue = {
    type: 'region',
    regions: [],
    markets: [],
  };
  if (!Array.isArray(options) || !Array.isArray(payload)) return result;

  // 勾选数据的所有的一级value
  const categoriesPayload: string[] = [];
  // 勾选数据的所有一级value 对应的 二级value
  const categoriesMetricsPayload: {
    [key: string]: string[];
  } = {};
  for (const payloadItem of payload) {
    if (!payloadItem) continue;
    const { dataSource } = analysisDataSourceMetricKey(payloadItem, joiner);

    categoriesPayload.push(dataSource);
    if (!Array.isArray(categoriesMetricsPayload[dataSource])) {
      categoriesMetricsPayload[dataSource] = [];
    }
    categoriesMetricsPayload[dataSource].push(payloadItem);
  }

  // 二级数据全部勾选的 一级数据
  const regions: Set<string> = new Set();
  let isRegion = true;
  // 遍历已勾选的一级数据
  for (const catetoryPayload of categoriesPayload) {
    if (!catetoryPayload) continue;
    // 获取当前已勾选的一级数据 对应options中 的一级数据
    let categoryOption = options.find((option) => option?.value === catetoryPayload);

    // 如果找不到对应的分类（可能被合并到其他分类），尝试在所有分类的 children 中查找这些指标
    if (!categoryOption) {
      const categoryMetricsPayload = categoriesMetricsPayload[catetoryPayload] || [];
      // 找到包含这些指标的分类
      for (const option of options) {
        const childrenValues = option?.children?.map((child) => child?.value) || [];
        const hasAllMetrics = categoryMetricsPayload.every((metric) => childrenValues.includes(metric));
        if (hasAllMetrics && categoryMetricsPayload.length > 0) {
          categoryOption = option;
          break;
        }
      }
      if (!categoryOption) {
        if (resolveMissingCategoryAsMarket) {
          isRegion = false;
        }
        continue;
      }
    }

    // 获取当前已勾选的一级数据 对应options的 一级数据的二级数据
    const categoryMetricsOption = categoryOption?.children?.filter((item) => !item?.disabled) || [];
    // 获取当前已勾选的一级数据 对应的 二级数据
    const categoryMetricsPayload = categoriesMetricsPayload[catetoryPayload] || [];
    // 如果options的二级数据，跟 当前已勾选的二级数据 长度不一样，则说明 存在 某个一级数据 是非全选的
    if (categoryMetricsOption?.length !== categoryMetricsPayload?.length) {
      isRegion = false;
    }
    // 如果options的二级数据，跟 当前已勾选的二级数据 长度一样，则说明 当前遍历中的一级数据 是全选的，需要push到regions中
    if (categoryMetricsOption?.length === categoryMetricsPayload?.length) {
      // 使用实际找到的 categoryOption 的 value，而不是 catetoryPayload
      regions.add(categoryOption.value);
    }
  }

  result.type = isRegion ? 'region' : 'market';
  result.regions = Array.from(regions);
  result.markets = [...payload];
  return result;
};

const getSortedValue = (
  payload: string[],
  options: OptionsItemType[],
  joiner: string,
): string[] => {
  if (!Array.isArray(options) || !Array.isArray(payload)) return [];
  const payloadTmp = cloneDeep(payload);
  payloadTmp.sort((a, b) => {
    const { dataSource: dataSourceA } = analysisDataSourceMetricKey(a, joiner);
    const { dataSource: dataSourceB } = analysisDataSourceMetricKey(b, joiner);
    const dataSourceIdxA = options.findIndex((option) => option?.value === dataSourceA);
    const dataSourceIdxB = options.findIndex((option) => option?.value === dataSourceB);
    if (dataSourceIdxA !== dataSourceIdxB) return dataSourceIdxA - dataSourceIdxB;
    const optionChildren = options?.[dataSourceIdxA]?.children || [];
    const metricIdxA = optionChildren?.findIndex((child) => child.value === a);
    const metricIdxB = optionChildren?.findIndex((child) => child.value === b);
    return metricIdxA - metricIdxB;
  });
  return payloadTmp;
};

const getOptionByOptionValue = (
  optionValue: string,
  options: OptionsItemType[],
): OptionsItemType | null => {
  if (!optionValue) return null;
  if (!Array.isArray(options)) return null;
  for (const option of options) {
    const children = option?.children;
    if (!Array.isArray(children)) continue;
    for (const child of children) {
      if (child?.value === optionValue) {
        return child;
      }
    }
  }
  return null;
};

interface ICascaderMetricSelectProps extends IBaseProps {
  locale?: 'en-US' | 'zh-CN' | (string & {});
  language?: ILanguage;
  buttonType?: '1' | '2' | '3' | '4';
  options: OptionsItemType[];
  // value格式为 一级code###二级code，通过###连接一级code、二级code
  value: string[];
  // value格式为 一级code###二级code，通过###连接一级code、二级code
  onChange?: (value: string[]) => void;
  // value格式为 一级code###二级code，通过###连接一级code、二级code
  onConfirm?: (value: string[]) => any;
  joiner?: string;
  // 筛选器标题前面的图标
  titleIcon?: string;
  resolveSecondLevelTitle?: (
    selectedRegionItem: OptionsItemType | undefined,
    locale: string,
  ) => string;
  dropdownBoxClassName?: string;
  dropdownOverlayClassName?: string;
  rightListWidth?: number;
  shortcutOptions?: ShortcutOptionType[];
}

const CascaderMetricSelect: React.FC<ICascaderMetricSelectProps> = (props) => {
  const {
    locale = 'en-US',
    language,
    buttonType = '1',
    options = [],
    value: valueProps,
    onChange,
    onConfirm,
    style,
    className,
    joiner = '###',
    loading,
    titleIcon,
    resolveSecondLevelTitle,
    dropdownBoxClassName,
    dropdownOverlayClassName,
    rightListWidth,
    shortcutOptions = [],
  } = props;
  const hasShortcutOptions = shortcutOptions?.length > 0;

  // console.log('CascaderMetricSelect', props);

  const { formatMessage } = useIntlExtend();

  // 中英切换
  const localeOptions = useMemo(() => {
    return options?.map((item) => {
      return {
        ...item,
        children: item.children?.map((subItem) => {
          return {
            ...subItem,
            text: formatMessage({ id: subItem.text }) as string,
          };
        }),
      };
    });
  }, [options, locale]);

  const value = useMemo<IRegionSelectValue>(() => {
    return getValueOfRegionSelect(valueProps, options, joiner);
  }, [valueProps, options, joiner]);
  const [draftValues, setDraftValues] = useState(valueProps);

  useEffect(() => {
    if (!hasShortcutOptions) {
      return;
    }
    setDraftValues(valueProps);
  }, [hasShortcutOptions, valueProps]);

  const draftValue = useMemo<IRegionSelectValue>(() => {
    return getValueOfRegionSelect(draftValues, options, joiner, hasShortcutOptions);
  }, [draftValues, hasShortcutOptions, options, joiner]);
  let selectorValue = value;
  if (hasShortcutOptions) {
    selectorValue = draftValue;
  }

  const availableShortcutOptions = useMemo(() => {
    const availableMetricValues = new Set<string>();
    for (const option of options) {
      for (const child of option?.children || []) {
        if (!child?.disabled) {
          availableMetricValues.add(child.value);
        }
      }
    }

    return shortcutOptions.map((item) => {
      const availableValues = (item?.value || []).filter((itemValue) =>
        availableMetricValues.has(itemValue),
      );
      return {
        ...item,
        disabled: item?.disabled || availableValues.length === 0,
        value: availableValues,
      };
    });
  }, [options, shortcutOptions]);

  const mergedShortcutOptions = useMemo(() => {
    return availableShortcutOptions.map((item) => {
      const shortcutValues = item?.disabled ? [] : item?.value || [];
      return {
        ...item,
        canClear: false,
        value: Array.from(new Set([...(draftValue?.markets || []), ...shortcutValues])),
      };
    });
  }, [availableShortcutOptions, draftValue?.markets]);

  const getButtonElement = useMemoizedFn(
    (payload: {
      labelText: string | React.ReactNodeArray;
      valueText: string | number;
      showIcon?: boolean;
      icon?: string;
      showTitleIcon?: boolean;
      titleIconSrc?: string;
    }) => {
      const { labelText, valueText, showIcon = false, icon = '', showTitleIcon = false, titleIconSrc = '' } = payload || {};

      return (
        <div className="dbc-region-select-button-wrap">
          {showTitleIcon && titleIconSrc && (
            <img
              className="dbc-region-select-title-icon"
              src={titleIconSrc}
              alt=""
            />
          )}
          <span className="dbd-filter-content-title-item-name">{labelText}</span>
          {showIcon && (
            <img
              className="dbc-region-select-item-icon"
              src={icon}
              alt=""
              style={{ width: 16, height: 16 }}
            />
          )}
          {/* <span>{valueText}</span> */}
          <span>
            {String(valueText)?.startsWith('dbi_') ? formatMessage({ id: valueText }) : valueText}
          </span>
        </div>
      );
    },
  );

  const button1 = useMemo<React.ReactElement>(() => {
    const type = selectorValue?.type || 'market';
    const regions = selectorValue?.regions || [];
    const markets = selectorValue?.markets || [];
    const customRegionText =
      buttonType === '4'
        ? formatMessage({ id: 'dbi-sm-filter-datasource' })
        : formatMessage({ id: 'dbi_label_metrics_category' });
    const labelText =
      type === 'region' ? customRegionText : formatMessage({ id: 'dbi_label_metrics' });
    let numText = Number.NaN;
    let valueText: string | number = '';
    let isShowDetail = false;
    let marketName = '';
    let marketIcon = '';
    if (type === 'market' && markets?.length === 1) {
      const market = getOptionByOptionValue(markets?.[0], options);
      isShowDetail = true;
      marketName = (locale === LANGUAGE_CN ? market?.textCN : market?.text) || market?.text || '';
      // @ts-ignore
      marketIcon = market?.icon;
    } else {
      numText = type === 'region' ? regions?.length : markets?.length;
    }

    valueText = isShowDetail ? marketName : numText;

    return getButtonElement({
      labelText,
      valueText,
      showIcon: isShowDetail,
      icon: marketIcon,
    });
  }, [locale, selectorValue, options, getButtonElement]);

  const button2 = useMemo<React.ReactElement>(() => {
    const type = value?.type || 'market';
    const regions = value?.regions || [];
    const markets = value?.markets || [];
    const labelText =
      type === 'region'
        ? formatMessage({ id: 'dbi-sm-filter-datasource' })
        : formatMessage({ id: 'dbi_device' });
    let numText = Number.NaN;
    let valueText: string | number = '';
    let isShowDetails = false;
    let isShowDetailsIcon = false;
    let detailsName = '';
    let detailsIcon = '';

    if (type === 'market') {
      if (markets?.length === 1) {
        const market = getOptionByOptionValue(markets?.[0], options);
        detailsName = (locale === LANGUAGE_CN ? market?.textCN : market?.text) || '';
        // @ts-ignore
        detailsIcon = market?.icon;
        isShowDetails = true;
      } else {
        numText = markets?.length;
      }
    } else {
      if (regions?.length === 1) {
        const region = options?.find((option) => option?.value === regions?.[0]);
        detailsName = (locale === LANGUAGE_CN ? region?.textCN : region?.text) || '';
        // @ts-ignore
        detailsIcon = region?.icon;
        isShowDetails = true;
        isShowDetailsIcon = true;
      } else {
        numText = regions?.length;
      }
    }

    valueText = isShowDetails ? detailsName : numText;

    return getButtonElement({
      labelText,
      valueText,
      showIcon: isShowDetailsIcon,
      icon: detailsIcon,
    });
  }, [locale, value, options, getButtonElement]);

  const button3 = useMemo<React.ReactElement>(() => {
    const type = value?.type || 'market';
    // const regions = value?.regions || [];
    const markets = value?.markets || [];
    const labelText =
      type === 'region'
        ? formatMessage({ id: 'dbi_detail_theme' })
        : formatMessage({ id: 'dbi_detail_theme' });
    let numText: any = '';
    let valueText: string | number = '';
    let isShowDetail = false;
    let marketName = '';
    const marketIcon = '';

    if (!markets?.length) {
      numText = (
        <span className="dbi-cascader-metric-select-placeholder" style={{ color: '#BBBDCB' }}>{formatMessage({ id: 'dbi_select_any_tag' })}</span>
      );
    } else if (markets?.length === 1) {
      const market = getOptionByOptionValue(markets?.[0], options);
      isShowDetail = true;
      marketName = (locale === LANGUAGE_CN ? market?.textCN : market?.text) || '';
      // @ts-ignore
      // marketIcon = market?.icon;
    } else {
      numText = markets?.length;
    }
    valueText = isShowDetail ? marketName : numText;
    return getButtonElement({
      labelText,
      valueText,
      showIcon: false,
      icon: marketIcon,
      showTitleIcon: !!titleIcon,
      titleIconSrc: titleIcon,
    });
  }, [locale, value, options, getButtonElement, titleIcon]);

  const button = useMemo<React.ReactElement>(() => {
    if (buttonType === '1') return button1;
    if (buttonType === '2') return button2;
    if (buttonType === '3') return button3;
    return button1;
  }, [buttonType, button1, button2]);

  const handleChange = (payload: any) => {
    const selectedValues = payload?.markets || [];
    if (hasShortcutOptions) {
      setDraftValues(selectedValues);
    }
    onChange?.(selectedValues);
  };

  const handleShortcutClick = (shortcutType: string) => {
    const shortcutOption = availableShortcutOptions.find((item) => item?.type === shortcutType);
    if (shortcutOption?.disabled) {
      return;
    }
    const shortcutValues = shortcutOption?.value || [];
    const isShortcutSelected = shortcutValues.every((item) => draftValue?.markets?.includes(item));
    if (!isShortcutSelected) {
      return;
    }
    const shortcutValueSet = new Set(shortcutValues);
    const selectedValues = draftValue.markets.filter((item) => !shortcutValueSet.has(item));
    setDraftValues(selectedValues);
    onChange?.(selectedValues);
  };

  const handleCloseRegionSelect = (payload: any) => {
    const valueSelect = payload?.markets || [];
    if (JSON.stringify(payload?.markets) === JSON.stringify(value?.markets)) {
      return;
    }
    onConfirm?.(getSortedValue(valueSelect, options, joiner));
  };

  // 添加默认展开已选第一个
  // 使用实际的 option category 而不是解析出的 dataSource
  const selectedRegionCur = useMemo(() => {
    const valueItem = selectorValue?.markets?.[0] || '';
    if (!valueItem) return undefined;

    // 从 options 中查找该指标实际所属的分类
    for (const option of options) {
      if (!Array.isArray(option?.children)) continue;
      const found = option.children.find((child) => child?.value === valueItem);
      if (found) {
        return option.value;
      }
    }

    // 如果找不到，返回 undefined，让 NewRegionSelect 使用默认值
    return undefined;
  }, [selectorValue, options]);

  return (
    <div style={style} className={classnames('dbi-cascader-metric-select search-item', className)}>
      {valueProps && (
        <NewRegionSelect
          locale={locale}
          language={merge(LOCALES, language)}
          radioOptions={RADIO_OPTIONS}
          button={button}
          // options={options}
          options={localeOptions}
          value={selectorValue}
          onChange={handleChange}
          shortcutOptions={mergedShortcutOptions}
          onShortcutClick={handleShortcutClick}
          onCloseReginSelect={handleCloseRegionSelect}
          isNotRegionIcon={true}
          displayDisabledStatusOfSelected
          boxClassName={classnames('dbi-region-selector-dropdown', dropdownBoxClassName)}
          overlayClassName={classnames('dbc-region-select-box', dropdownOverlayClassName, {
            'dbi-cascader-metric-select-shortcut-overlay': mergedShortcutOptions.length > 0,
          })}
          leftBubblePlacement={'bottom-start'}
          leftBubblePlacementOffset={['60%', '10']}
          rightBubblePlacement={'bottom-start'}
          rightBubblePlacementOffset={['60%', '10']}
          // ifNeedSelectedFirstRegion={true}
          defaultSelectedRegion={selectedRegionCur}
          loading={loading}
          tagNumber={0}
          rightListWidth={rightListWidth}
          resolveSecondLevelTitle={resolveSecondLevelTitle}
        />
      )}
    </div>
  );
};

export default CascaderMetricSelect;
